import {
  FlexBox,
  FormField,
  FormGrid,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  SelectionList,
  Spinner,
  Text,
} from '@dixa/lib-shared-habla';
import { isResponseWithData } from 'lib-shared-auth';
import { ampli } from 'lib-shared-tracking';
import noop from 'lodash/noop';
import moment from 'moment';
import { useMemo } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';

import { mimChatbotsApi } from 'apis/chatbots';
import {
  KnowledgeBase,
  knowledgeBasesApi,
  KnowledgeBaseType,
} from 'apis/knoledgeBases';
import { HookFormSelectionList, HookFormText } from 'components/hookForm';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { createAppModal } from 'utils/modal';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useAppNavigate } from 'utils/url/useAppNavigate';

import { MimUserAvatarControl } from '../mimUserAvatarControl/mimUserAvatarControl';

import styles from './chatbotCreateModal.module.scss';

const formId = 'mim-chatbot-create';

type FieldValues = {
  name: string;
  userName: string;
  userAvatarUrl: string;
  knowledgeBases: KnowledgeBase[];
};

const emptyArray: KnowledgeBase[] = [];

export const ChatbotCreateModal = createAppModal(
  { type: 'settings:createChatbotModal' },
  ({ isOpen, hide, modal }): JSX.Element => {
    const [createChatbot, { isLoading: isCreating }] =
      mimChatbotsApi.useCreateMimChatbotMutation();
    const numberOfChatbots = modal.arg.numberOfChatbots;

    const organizationId = useAppSelector(
      (state) => state.settings.organization.organization.id,
    );
    const flags = useFeatureFlag(['knowledge-access']);
    const knowledgeSource: KnowledgeBaseType[] = useMemo(() => {
      return flags.knowledgeAccess ? ['elevio', 'dixa_knowledge'] : ['elevio'];
    }, [flags.knowledgeAccess]);

    const { knowledgeBases, isLoading } =
      knowledgeBasesApi.useReadKnowledgeBasesQuery(
        { organizationId: organizationId, source: knowledgeSource },
        {
          selectFromResult: ({ data, ...rest }) => ({
            ...rest,
            knowledgeBases:
              data && Array.isArray(data.data.knowledgeBases)
                ? [...data.data.knowledgeBases].reverse()
                : emptyArray,
          }),
        },
      );

    const navigate = useAppNavigate();
    const notify = useNotifications();

    const form = useForm<FieldValues>({
      defaultValues: {
        name: '',
        userName: '',
        userAvatarUrl: '',
        knowledgeBases: [],
      },
      mode: 'all',
    });

    const onSubmit: SubmitHandler<FieldValues> = async ({
      name,
      userName,
      userAvatarUrl,
      knowledgeBases,
    }) => {
      try {
        const response = await createChatbot({
          organizationId,
          name,
          knowledgeBases: [
            ...knowledgeBases.map((kb) => ({
              id: kb.id,
              includeInternalArticles: false,
            })),
          ],
          user: {
            name: userName,
            avatarUrl: userAvatarUrl,
          },
        }).unwrap();

        if (isResponseWithData(response)) {
          notify(
            {
              title: 'Success',
              text: `"${response.data?.name || 'Chatbot'}" has been updated.`,
              icon: 'i-done',
              time: 5000,
            },
            NotificationType.Success,
          );
          ampli.chatbotCreated({
            numberOfChatbots: numberOfChatbots + 1,
            chatbotType: 'mim',
          });
          hide?.();
          navigate({ path: '/settings/chatbots/mim' });
        }
      } catch (error) {
        notify(
          {
            title: 'Error while updating chatbot.',
            text: 'Something went wrong.',
            icon: 'exclamation',
            className: 'danger',
            time: 5000,
          },
          NotificationType.Error,
        );
      }
    };

    const findKbById = (
      id: string,
      items: KnowledgeBase[],
    ): KnowledgeBase | undefined => {
      return items.find((i) => i.id === id);
    };

    const isSyncing = (kb: KnowledgeBase) => kb.state === 'syncing';

    const getFilteredKnowledgeBases = (
      knowledgeBases: KnowledgeBase[],
      type: KnowledgeBaseType,
      title: string,
    ) => {
      const filtered = knowledgeBases.filter((item) => item.source === type);

      if (filtered.length === 0) return null;

      return (
        <SelectionList.Section key={type} title={title}>
          {filtered.map((kb) => (
            <SelectionList.Item key={kb.id} value={kb}>
              <Text.Paragraph>{kb.name}</Text.Paragraph>
              <Text.Paragraph subdued variant="4">
                {moment(kb.created_at).format('DD/MM/YYYY HH:mm:ss')}{' '}
                {isSyncing(kb) && <span>- Knowledge base sync in process</span>}
              </Text.Paragraph>
            </SelectionList.Item>
          ))}
        </SelectionList.Section>
      );
    };

    return (
      <Modal isOpen={isOpen} onClose={hide} className={styles.modal}>
        <ModalHead title="Create chatbot" onClose={hide}></ModalHead>
        <ModalBody>
          {isLoading ? (
            <FlexBox flex="1" justifyContent="center" alignItems="center">
              <Spinner />
            </FlexBox>
          ) : (
            <form
              id={formId}
              noValidate
              tabIndex={-1}
              onSubmit={form.handleSubmit(onSubmit)}
            >
              <Text.Paragraph variant="1" wrap="nowrap" withMarginBottom>
                Identify your chatbot
              </Text.Paragraph>
              <FormGrid>
                <FormField>
                  <FormProvider {...form}>
                    <MimUserAvatarControl />
                  </FormProvider>
                </FormField>
                <FormField>
                  <HookFormText
                    name="name"
                    label="Internal reference"
                    control={form.control}
                    rules={{
                      required: 'Internal reference is required',
                    }}
                    helpIcon={{
                      text: 'The reference of your bot for internal purposes.',
                    }}
                  />
                </FormField>

                <FormField>
                  <HookFormText
                    name="userName"
                    label="Public display name"
                    control={form.control}
                    rules={{
                      required: 'Public display name is required',
                    }}
                    helpIcon={{
                      text: 'The name that your users will see while interacting with the bot.',
                    }}
                  />
                </FormField>
                <FormField>
                  <HookFormSelectionList
                    control={form.control}
                    name="knowledgeBases"
                    label="Select your data source"
                    rules={{ required: 'Data source is required' }}
                    componentProps={{
                      multiple: true,
                      equalityFn: (item1, item2) => item1.id === item2.id,
                      getItemTitleByValue: (
                        kb: KnowledgeBase | null | undefined,
                      ) => {
                        const selectedChatbot = findKbById(
                          kb!.id,
                          knowledgeBases,
                        );
                        return (
                          <Text.Paragraph>
                            {selectedChatbot?.name ?? ''}
                          </Text.Paragraph>
                        );
                      },
                    }}
                  >
                    {getFilteredKnowledgeBases(
                      knowledgeBases,
                      'elevio',
                      'Elevio',
                    )}
                    {getFilteredKnowledgeBases(
                      knowledgeBases,
                      'dixa_knowledge',
                      'Dixa Knowledge',
                    )}
                    {getFilteredKnowledgeBases(
                      knowledgeBases,
                      'website',
                      'Website',
                    )}
                  </HookFormSelectionList>
                </FormField>
              </FormGrid>
            </form>
          )}
        </ModalBody>

        <ModalFoot
          cancelName="Cancel"
          cancelOnClick={hide}
          confirmName="Create chatbot"
          confirmOnClick={noop}
          confirmProps={{
            isLoading: isCreating,
            loadingIndicator: 'Creating...',
            type: 'submit',
            form: formId,
            disabled:
              isLoading ||
              !form.formState.isValid ||
              form.formState.isSubmitting ||
              !form.formState.isDirty,
          }}
        />
      </Modal>
    );
  },
);
